/**
 * Tarefas no modelo da base Notion "Gestão - BP": etapas, prioridade P0–P2,
 * faixas e vistas. Alerta e faixa vêm SEMPRE do servidor (campos da TaskView);
 * aqui só há rótulos, cores, agrupamento e os filtros da URL.
 */
import type { Task } from '@/types/tasks'

export type TaskStage = 'A_FAZER' | 'EM_ANDAMENTO' | 'AGUARDANDO' | 'CONCLUIDA' | 'ARQUIVADA'
export type PriorityLevel = 'P0' | 'P1' | 'P2'
export type TaskBand = 'VENCIDAS' | 'HOJE' | 'PROXIMOS_3' | 'PROXIMOS_7' | 'DEMAIS'
export type BoardView = 'minha-semana' | 'tabela' | 'quadro' | 'escritorio' | 'calendario' | 'arquivo'

export interface TaskAlert {
  nivel: 'VENCIDA' | 'HOJE' | 'EM_BREVE'
  cor: 'vermelho' | 'laranja' | 'amarelo'
  texto: string
  dias: number
}

/** Tarefa decorada pelo servidor (GET /api/tasks/painel e demais rotas). */
export interface TaskView extends Task {
  etapa: TaskStage
  etapa_label: string
  prioridade: PriorityLevel
  data_referencia: string | null
  faixa: TaskBand
  alerta: TaskAlert | null
  retorno_alerta: string | null
  em_revisao: boolean
  prazo_fatal?: string | null
  prazo_interno?: string | null
  waiting_on?: string | null
  waiting_for?: string | null
  waiting_until?: string | null
  attachment_url?: string | null
  carteira_id?: string | null
  linked_event_id?: string | null
}

export const STAGES: Array<{ value: TaskStage; label: string }> = [
  { value: 'A_FAZER', label: 'A fazer' },
  { value: 'EM_ANDAMENTO', label: 'Em andamento' },
  { value: 'AGUARDANDO', label: 'Aguardando' },
  { value: 'CONCLUIDA', label: 'Concluída' },
  { value: 'ARQUIVADA', label: 'Arquivada' },
]

/** Colunas do quadro (Arquivada sai das vistas; arquiva-se pela gaveta). */
export const BOARD_STAGES = STAGES.filter((s) => s.value !== 'ARQUIVADA')

export const BANDS: Array<{ value: TaskBand; label: string }> = [
  { value: 'VENCIDAS', label: 'Vencidas' },
  { value: 'HOJE', label: 'Vence hoje' },
  { value: 'PROXIMOS_3', label: 'Próximos 3 dias' },
  { value: 'PROXIMOS_7', label: 'Próximos 7 dias' },
  { value: 'DEMAIS', label: 'Demais' },
]

export const PRIORITY_LEVELS: Array<{ value: PriorityLevel; label: string }> = [
  { value: 'P0', label: 'P0 · alta' },
  { value: 'P1', label: 'P1 · normal' },
  { value: 'P2', label: 'P2 · baixa' },
]

export const VIEWS: Array<{ value: BoardView; label: string }> = [
  { value: 'minha-semana', label: 'Minha semana' },
  { value: 'tabela', label: 'Tabela' },
  { value: 'quadro', label: 'Quadro' },
  { value: 'escritorio', label: 'Prazos do escritório' },
  { value: 'calendario', label: 'Calendário de prazos' },
  { value: 'arquivo', label: 'Arquivo' },
]

export const ALERT_CLASSES: Record<TaskAlert['cor'], string> = {
  vermelho: 'bg-destructive/20 font-semibold text-destructive',
  laranja: 'bg-destructive/10 text-destructive',
  amarelo: 'bg-primary/10 text-primary',
}

export const PRIORITY_CLASSES: Record<PriorityLevel, string> = {
  P0: 'border-destructive/50 text-destructive',
  P1: 'border-primary/30 text-primary',
  P2: 'border-muted-foreground/30 text-muted-foreground',
}

// ── Filtros na URL ───────────────────────────────────────────────────────────

export interface BoardFilters {
  vista: BoardView
  /** id do responsável ou 'eu' */
  responsavel?: string
  carteira?: string
  prioridade?: PriorityLevel
  processo?: string
  busca?: string
  /** id do cliente (atalho da ficha do cliente) */
  cliente?: string
  /** Calendário: mês AAAA-MM */
  mes?: string
}

export const DEFAULT_VIEW: BoardView = 'minha-semana'
const OPTIONAL = ['responsavel', 'carteira', 'prioridade', 'processo', 'busca', 'cliente', 'mes'] as const

export function isView(value: string | null): value is BoardView {
  return VIEWS.some((v) => v.value === value)
}

export function boardFiltersFromSearch(params: URLSearchParams): BoardFilters {
  const vista = params.get('vista')
  const filters: BoardFilters = { vista: isView(vista) ? vista : DEFAULT_VIEW }
  for (const key of OPTIONAL) {
    const value = params.get(key)?.trim()
    if (!value) continue
    if (key === 'prioridade' && !PRIORITY_LEVELS.some((p) => p.value === value)) continue
    if (key === 'mes' && !/^\d{4}-\d{2}$/.test(value)) continue
    ;(filters as unknown as Record<string, string>)[key] = value
  }
  return filters
}

/**
 * Grava na URL só o que difere do padrão e preserva parâmetros que não são
 * filtros (ex.: `tarefa`, que abre a gaveta).
 */
export function boardFiltersToSearch(filters: BoardFilters, current?: URLSearchParams): URLSearchParams {
  const params = new URLSearchParams()
  current?.forEach((value, key) => {
    if (key !== 'vista' && !(OPTIONAL as readonly string[]).includes(key)) params.set(key, value)
  })
  if (filters.vista !== DEFAULT_VIEW) params.set('vista', filters.vista)
  for (const key of OPTIONAL) {
    const value = filters[key]
    if (value) params.set(key, value)
  }
  return params
}

/** Parâmetros de GET /api/tasks/painel. */
export function toPainelParams(filters: BoardFilters): Record<string, string> {
  const params: Record<string, string> = { vista: filters.vista }
  if (filters.responsavel) params.responsavel = filters.responsavel
  if (filters.carteira) params.carteira = filters.carteira
  if (filters.prioridade) params.prioridade = filters.prioridade
  if (filters.processo) params.processo = filters.processo
  if (filters.busca) params.busca = filters.busca
  if (filters.cliente) params.cliente = filters.cliente
  if (filters.vista === 'calendario' && filters.mes) {
    const [y, m] = filters.mes.split('-').map(Number)
    params.de = `${filters.mes}-01`
    params.ate = `${filters.mes}-${String(new Date(Date.UTC(y, m, 0)).getUTCDate()).padStart(2, '0')}`
  }
  return params
}

// ── Agrupamento ──────────────────────────────────────────────────────────────

export function groupBy<K extends string>(tasks: TaskView[], key: (t: TaskView) => K, order: readonly K[]): Array<{ key: K; tasks: TaskView[] }> {
  const map = new Map<K, TaskView[]>()
  for (const t of tasks) {
    const k = key(t)
    map.set(k, [...(map.get(k) ?? []), t])
  }
  const known = order.filter((k) => map.has(k)).map((k) => ({ key: k, tasks: map.get(k)! }))
  const rest = [...map.keys()].filter((k) => !order.includes(k)).map((k) => ({ key: k, tasks: map.get(k)! }))
  return [...known, ...rest]
}

export const byBand = (tasks: TaskView[]) => groupBy(tasks, (t) => t.faixa, BANDS.map((b) => b.value))
export const byStage = (tasks: TaskView[]) => groupBy(tasks, (t) => t.etapa, STAGES.map((s) => s.value))
export const byResponsible = (tasks: TaskView[]) => groupBy(tasks, (t) => t.assigned_user_name || 'Sem responsável', [])

export const bandLabel = (band: TaskBand) => BANDS.find((b) => b.value === band)?.label ?? band
export const stageLabel = (stage: TaskStage) => STAGES.find((s) => s.value === stage)?.label ?? stage

/** dd/mm/aaaa de uma data AAAA-MM-DD (sem fuso). */
export function formatDay(value?: string | null): string {
  if (!value) return '—'
  const [y, m, d] = value.slice(0, 10).split('-')
  return d && m && y ? `${d}/${m}/${y}` : '—'
}
